import { c as _c } from "react/compiler-runtime";
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import useShowroomLocator from 'hooks/useShowroomLocator';
import useRetailStores from 'hooks/useRetailStores';
import useIsBrowser from 'hooks/useIsBrowser';
import useZipCodeFromUser from 'global-state/zipCodeFromUser/useZipCodeFromUser';
import AdminPlaceholder from 'components/consumer/AdminPlaceholder';
import StoreLocatorSearch from './StoreLocatorSearch';
import StoreLocatorResultsList from './StoreLocatorResultsList';
import StoreLocatorViewAll from './StoreLocatorViewAll';
import StoreLocatorNoResults from './StoreLocatorNoResults';
import StoreLocatorMap from './StoreLocatorMap';
import { isPhysicalShowroom, toStoreEntry } from './helpers';

// CON-8186: useShowroomLocator already strips non-showroom store records
// (Virtual Store, CS Sales) from zipcode search results, which covers the
// header, footer, and mobile nav. useRetailStores intentionally isn't filtered
// globally — other consumers (useCurrentShowroom, DynamicShowroomsList) may
// legitimately want those records, and store-mode resolution depends on them —
// so we strip them locally here for the View All list/map.

const VirtualShowroomBlock = () => {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <h3 className="text-base text-brand font-bold mb-1">Virtual Showroom</h3>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <br />;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="mt-6 pt-4 border-t border-gray-light6 text-sm text-gray">{t0}<p>Cannot go to a showroom?{t1}Open the chat during business hours<br />or</p><a href="/showrooms/virtual/" data-xc="StoreLocatorVirtualShowroomLink" className="text-brand underline hover:text-gray-dark">Book a Virtual Showroom Appointment</a></div>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
};
const StoreList = t0 => {
  const $ = _c(11);
  const {
    mode,
    allStores,
    allStoresLoading,
    allStoresError,
    searchStores,
    searchLoading,
    searchError,
    hasSearched,
    onActivate
  } = t0;
  if (mode === "viewAll") {
    if (allStoresLoading) {
      let t1;
      if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
        t1 = <div className="py-4 text-sm text-gray-light1">Loading showrooms...</div>;
        $[0] = t1;
      } else {
        t1 = $[0];
      }
      return t1;
    }
    if (allStoresError) {
      let t1;
      if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
        t1 = <div className="py-4 text-sm text-gray-light1">Unable to load showrooms right now.</div>;
        $[1] = t1;
      } else {
        t1 = $[1];
      }
      return t1;
    }
    let t1;
    if ($[2] !== allStores || $[3] !== onActivate) {
      t1 = <StoreLocatorViewAll stores={allStores} onActivate={onActivate} />;
      $[2] = allStores;
      $[3] = onActivate;
      $[4] = t1;
    } else {
      t1 = $[4];
    }
    return t1;
  }
  if (searchLoading) {
    let t1;
    if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div className="py-4 text-sm text-gray-light1">Searching...</div>;
      $[5] = t1;
    } else {
      t1 = $[5];
    }
    return t1;
  }
  if (searchError && (!searchStores || searchStores.length === 0)) {
    let t1;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div className="py-4 text-sm text-gray-light1">Unable to search right now. Please try again.</div>;
      $[6] = t1;
    } else {
      t1 = $[6];
    }
    return t1;
  }
  if (!hasSearched) {
    return null;
  }
  if (!searchStores || searchStores.length === 0) {
    let t1;
    if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <StoreLocatorNoResults />;
      $[7] = t1;
    } else {
      t1 = $[7];
    }
    return t1;
  }
  let t1;
  if ($[8] !== onActivate || $[9] !== searchStores) {
    t1 = <StoreLocatorResultsList stores={searchStores} onActivate={onActivate} />;
    $[8] = onActivate;
    $[9] = searchStores;
    $[10] = t1;
  } else {
    t1 = $[10];
  }
  return t1;
};
StoreList.propTypes = {
  mode: PropTypes.oneOf(['viewAll', 'search']).isRequired,
  allStores: PropTypes.array,
  allStoresLoading: PropTypes.bool,
  allStoresError: PropTypes.any,
  searchStores: PropTypes.array,
  searchLoading: PropTypes.bool,
  searchError: PropTypes.any,
  hasSearched: PropTypes.bool,
  onActivate: PropTypes.func.isRequired
};
const StoreLocatorPage = t0 => {
  const $ = _c(49);
  const {
    isAdmin: t1
  } = t0;
  const isAdmin = t1 === undefined ? false : t1;
  const storedZip = useZipCodeFromUser();
  const isBrowser = useIsBrowser();
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      limit: 50
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const {
    stores: searchStores,
    loading: searchLoading,
    error: searchError,
    hasSearched,
    handleSearch,
    handleSearchByPlace,
    handleViewAll
  } = useShowroomLocator(t2);
  const {
    stores: rawAllStores,
    loading: allStoresLoading,
    error: allStoresError
  } = useRetailStores();
  let t3;
  if ($[1] !== rawAllStores) {
    t3 = rawAllStores || [];
    $[1] = rawAllStores;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== t3) {
    t4 = t3.filter(isPhysicalShowroom);
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const allStores = t4;
  const lastAppliedZipRef = useRef(null);
  const [activeStoreId, setActiveStoreId] = useState(null);
  const [zipcode, setZipcode] = useState("");
  const mode = !isBrowser || !hasSearched ? "viewAll" : "search";
  let t5;
  let t6;
  if ($[5] !== handleSearch || $[6] !== storedZip) {
    t5 = () => {
      if (!storedZip || lastAppliedZipRef.current === storedZip) {
        return;
      }
      lastAppliedZipRef.current = storedZip;
      setZipcode(storedZip);
      handleSearch(storedZip);
    };
    t6 = [storedZip, handleSearch];
    $[5] = handleSearch;
    $[6] = storedZip;
    $[7] = t5;
    $[8] = t6;
  } else {
    t5 = $[7];
    t6 = $[8];
  }
  useEffect(t5, t6);
  let t7;
  bb0: {
    if (mode === "viewAll") {
      let t8;
      if ($[9] !== allStores) {
        t8 = allStores || [];
        $[9] = allStores;
        $[10] = t8;
      } else {
        t8 = $[10];
      }
      t7 = t8;
      break bb0;
    }
    let t8;
    if ($[11] !== searchStores) {
      t8 = (searchStores || []).map(toStoreEntry).filter(Boolean).map(_temp);
      $[11] = searchStores;
      $[12] = t8;
    } else {
      t8 = $[12];
    }
    const searched = t8;
    if (searched.length === 0) {
      let t9;
      if ($[13] !== allStores) {
        t9 = allStores || [];
        $[13] = allStores;
        $[14] = t9;
      } else {
        t9 = $[14];
      }
      t7 = t9;
      break bb0;
    }
    t7 = searched;
  }
  const mapStores = t7;
  let t8;
  if ($[15] !== handleSearch) {
    t8 = nextZip => {
      lastAppliedZipRef.current = nextZip;
      setZipcode(nextZip);
      handleSearch(nextZip);
      setActiveStoreId(null);
    };
    $[15] = handleSearch;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const onSearch = t8;
  let t9;
  if ($[17] !== handleSearchByPlace) {
    t9 = place => {
      handleSearchByPlace(place, {
        onZipResolved: resolvedZip => {
          lastAppliedZipRef.current = resolvedZip;
        }
      });
      setActiveStoreId(null);
    };
    $[17] = handleSearchByPlace;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const onSearchByPlace = t9;
  let t10;
  if ($[19] !== handleViewAll || $[20] !== storedZip) {
    t10 = () => {
      lastAppliedZipRef.current = storedZip;
      setZipcode("");
      handleViewAll();
      setActiveStoreId(null);
    };
    $[19] = handleViewAll;
    $[20] = storedZip;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  const onViewAll = t10;
  if (isAdmin) {
    let t11;
    if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = <AdminPlaceholder className="!min-h-[470px]">Store Locator Page Admin Placeholder</AdminPlaceholder>;
      $[22] = t11;
    } else {
      t11 = $[22];
    }
    return t11;
  }
  let t11;
  if ($[23] !== onSearch || $[24] !== onSearchByPlace || $[25] !== onViewAll || $[26] !== zipcode) {
    t11 = <StoreLocatorSearch zipcode={zipcode} onZipcodeChange={setZipcode} onSearch={onSearch} onSearchByPlace={onSearchByPlace} onViewAll={onViewAll} />;
    $[23] = onSearch;
    $[24] = onSearchByPlace;
    $[25] = onViewAll;
    $[26] = zipcode;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== allStores || $[29] !== allStoresError || $[30] !== allStoresLoading || $[31] !== hasSearched || $[32] !== mode || $[33] !== searchError || $[34] !== searchLoading || $[35] !== searchStores) {
    t12 = <StoreList mode={mode} allStores={allStores} allStoresLoading={allStoresLoading} allStoresError={allStoresError} searchStores={searchStores} searchLoading={searchLoading} searchError={searchError} hasSearched={hasSearched} onActivate={setActiveStoreId} />;
    $[28] = allStores;
    $[29] = allStoresError;
    $[30] = allStoresLoading;
    $[31] = hasSearched;
    $[32] = mode;
    $[33] = searchError;
    $[34] = searchLoading;
    $[35] = searchStores;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  let t13;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <VirtualShowroomBlock />;
    $[37] = t13;
  } else {
    t13 = $[37];
  }
  let t14;
  if ($[38] !== t12) {
    t14 = <aside className="w-full lg:w-80 lg:flex-shrink-0 px-4 py-4 lg:overflow-y-auto">{t12}{t13}</aside>;
    $[38] = t12;
    $[39] = t14;
  } else {
    t14 = $[39];
  }
  let t15;
  if ($[40] !== activeStoreId || $[41] !== mapStores) {
    t15 = <div className="flex-1 h-96 lg:h-full"><StoreLocatorMap stores={mapStores} activeStoreId={activeStoreId} onMarkerClick={setActiveStoreId} /></div>;
    $[40] = activeStoreId;
    $[41] = mapStores;
    $[42] = t15;
  } else {
    t15 = $[42];
  }
  let t16;
  if ($[43] !== t14 || $[44] !== t15) {
    t16 = <div className="w-full flex flex-col lg:flex-row lg:flex-1 lg:min-h-0">{t14}{t15}</div>;
    $[43] = t14;
    $[44] = t15;
    $[45] = t16;
  } else {
    t16 = $[45];
  }
  let t17;
  if ($[46] !== t11 || $[47] !== t16) {
    t17 = <section className="w-full flex flex-col lg:h-screen">{t11}{t16}</section>;
    $[46] = t11;
    $[47] = t16;
    $[48] = t17;
  } else {
    t17 = $[48];
  }
  return t17;
};
StoreLocatorPage.propTypes = {
  isAdmin: PropTypes.bool
};
export default StoreLocatorPage;
function _temp(entry) {
  return entry.store;
}